// API Service for Appointigo Frontend
import api from "./api";
import {
  Provider,
  Service,
  Location,
  Appointment,
  QueueEntry,
  ApiResponse,
  PaginatedResponse,
  SearchFilters,
  BookingFormData,
  QueueJoinFormData,
  AvailableSlot,
} from "../types/api.types";

// Location Services
export const locationService = {
  getAll: async (params?: { city?: string; isActive?: boolean }) => {
    const response = await api.get<PaginatedResponse<Location>>("/locations", {
      params,
    });
    return response.data;
  },

  getById: async (id: string) => {
    const response = await api.get<ApiResponse<Location>>(`/locations/${id}`);
    return response.data;
  },

  getNearby: async (lat: number, lng: number, maxDistance: number = 10) => {
    const response = await api.get<PaginatedResponse<Location>>(
      "/locations/nearby",
      {
        params: { lat, lng, maxDistance },
      }
    );
    return response.data;
  },

  getByProvider: async (providerId: string) => {
    const response = await api.get<ApiResponse<Location[]>>(
      `/locations/provider/${providerId}`
    );
    return response.data;
  },
};

// Provider Services
export const providerService = {
  getAll: async (params?: {
    location?: string;
    category?: string;
    specialization?: string;
    isActive?: boolean;
    published?: boolean;
    city?: string;
    // Pagination
    page?: number;
    limit?: number;
    // Location-based params for distance calculation
    latitude?: number;
    longitude?: number;
    maxDistance?: number; // in meters
    sortByDistance?: boolean;
  }) => {
    // Default to only published providers for customer-facing frontend
    const queryParams = { published: true, ...params };
    const response = await api.get<PaginatedResponse<Provider>>("/providers", {
      params: queryParams,
    });
    return response.data;
  },

  getNearby: async (params: {
    latitude: number;
    longitude: number;
    maxDistance?: number; // in meters, default 10km
    limit?: number;
    category?: string;
    type?: string;
  }) => {
    const response = await api.get<ApiResponse<Provider[]>>("/providers/nearby", {
      params,
    });
    return response.data;
  },

  getTopRatedByCity: async (params: {
    latitude: number;
    longitude: number;
    city?: string;
    limit?: number;
  }) => {
    const response = await api.get<ApiResponse<Provider[]> & { city: string | null }>(
      "/providers/top-rated-by-city",
      { params }
    );
    return response.data;
  },

  getById: async (id: string) => {
    const response = await api.get<ApiResponse<Provider>>(`/providers/${id}`);
    return response.data;
  },

  search: async (
    query: string,
    filters?: {
      city?: string;
      category?: string;
      type?: string;
      locationId?: string;
      published?: boolean;
      page?: number;
      limit?: number;
    }
  ) => {
    // Default to only published providers for customer-facing frontend
    const queryParams = { q: query, published: true, ...filters };
    const response = await api.get<PaginatedResponse<Provider>>(
      "/providers/search",
      {
        params: queryParams,
      }
    );
    return response.data;
  },

  getAvailability: async (
    providerId: string,
    date: string,
    locationId: string
  ) => {
    const response = await api.get<ApiResponse<AvailableSlot>>(
      `/providers/${providerId}/availability`,
      {
        params: { date, locationId },
      }
    );
    return response.data;
  },

  getAvailabilityRange: async (
    providerId: string,
    startDate: string,
    endDate: string,
    locationId: string
  ) => {
    const response = await api.get<ApiResponse<AvailableSlot[]>>(
      `/providers/${providerId}/availability/range`,
      {
        params: { startDate, endDate, locationId },
      }
    );
    return response.data;
  },
};

// Service Services
export const serviceService = {
  getAll: async (params?: {
    location?: string;
    category?: string;
    isActive?: boolean;
    minPrice?: number;
    maxPrice?: number;
  }) => {
    const response = await api.get<PaginatedResponse<Service>>("/services", {
      params,
    });
    return response.data;
  },

  getById: async (id: string) => {
    const response = await api.get<ApiResponse<Service>>(`/services/${id}`);
    return response.data;
  },

  getCategories: async () => {
    const response = await api.get<ApiResponse<string[]>>(
      "/services/categories"
    );
    return response.data;
  },

  search: async (
    query: string,
    filters?: { category?: string; providerType?: string }
  ) => {
    const response = await api.get<ApiResponse<Service[]>>("/services/search", {
      params: { q: query, ...filters },
    });
    return response.data;
  },

  getByCategory: async (category: string) => {
    const response = await api.get<ApiResponse<Service[]>>(
      `/services/category/${category}`
    );
    return response.data;
  },

  getPopular: async (limit: number = 10) => {
    const response = await api.get<ApiResponse<Service[]>>(
      "/services/popular",
      {
        params: { limit },
      }
    );
    return response.data;
  },

  getByProvider: async (providerId: string) => {
    const response = await api.get<ApiResponse<Service[]>>(
      `/services/provider/${providerId}`
    );
    return response.data;
  },

  getLocationServices: async (locationId: string): Promise<Service[]> => {
    const res = await api.get<ApiResponse<Service[]> | Service[]>(
      `/services/location/${locationId}`
    );
    return (
      (res.data as ApiResponse<Service[]>)?.data || (res.data as Service[])
    );
  },
};

// Appointment Services
export const appointmentService = {
  getUserAppointments: async (userId: string, params?: {
    status?: string;
    upcoming?: boolean;
    dateFrom?: string;
    dateTo?: string;
    search?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) => {
    const response = await api.get<PaginatedResponse<Appointment>>(
      `/appointments/user/${userId}`,
      { params }
    );
    return response.data;
  },
  create: async (data: BookingFormData) => {
    const response = await api.post<ApiResponse<Appointment>>(
      "/appointments",
      data
    );
    return response.data;
  },

  getById: async (id: string) => {
    const response = await api.get<ApiResponse<Appointment>>(
      `/appointments/${id}`
    );
    return response.data;
  },

  getByToken: async (token: string) => {
    const response = await api.get<ApiResponse<Appointment>>(
      `/appointments/token/${token}`
    );
    return response.data;
  },

  getAll: async (params?: {
    status?: string;
    providerId?: string;
    locationId?: string;
    userId?: string;
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    limit?: number;
  }) => {
    const response = await api.get<PaginatedResponse<Appointment>>(
      "/appointments",
      { params }
    );
    return response.data;
  },

  update: async (id: string, data: Partial<BookingFormData>) => {
    const response = await api.put<ApiResponse<Appointment>>(
      `/appointments/${id}`,
      data
    );
    return response.data;
  },

  updateStatus: async (id: string, status: string, reason?: string) => {
    const response = await api.put<ApiResponse<Appointment>>(
      `/appointments/${id}/status`,
      {
        status,
        reason,
      }
    );
    return response.data;
  },

  cancel: async (id: string, reason?: string) => {
    const response = await api.put<ApiResponse<Appointment>>(
      `/appointments/${id}/cancel`,
      {
        reason,
        source: 'website',
      }
    );
    return response.data;
  },

  getCancelPreview: async (id: string): Promise<{
    cancelledBy: string;
    refundAmount: number;
    currency: string;
    refundPolicy: string;
    cancellationDeadlineHours: number;
    hoursBeforeAppointment: number;
    isPastAppointmentTime: boolean;
    isPastDeadline: boolean;
    isFullRefund: boolean;
    reason: string;
  }> => {
    const response = await api.get<ApiResponse<any>>(
      `/appointments/${id}/cancel-preview`,
      { params: { source: "website" } }
    );
    return response.data.data;
  },

  reschedule: async (
    id: string,
    newSlot: { date: string; start: string; end: string }
  ) => {
    const response = await api.put<ApiResponse<Appointment>>(
      `/appointments/${id}/reschedule`,
      {
        date: newSlot.date,
        start: newSlot.start,
        end: newSlot.end,
      }
    );
    return response.data;
  },

  checkAvailability: async (
    providerId: string,
    date: string,
    startTime: string
  ) => {
    const response = await api.post<ApiResponse<{ available: boolean }>>(
      "/appointments/check-availability",
      {
        providerId,
        date,
        startTime,
      }
    );
    return response.data;
  },

  getUpcoming: async (userId: string) => {
    const response = await api.get<PaginatedResponse<Appointment>>(
      "/appointments/upcoming",
      {
        params: { userId },
      }
    );
    return response.data;
  },

  getHistory: async (userId: string, page: number = 1, limit: number = 10) => {
    const response = await api.get<PaginatedResponse<Appointment>>(
      "/appointments/history",
      {
        params: { userId, page, limit },
      }
    );
    return response.data;
  },

  getAvailableSlots: async (
    providerId: string,
    locationId: string,
    serviceId: string,
    date: string
  ) => {
    // Get client timezone for accurate current time filtering
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    const response = await api.get<
      ApiResponse<Array<{ start: string; end: string; available: boolean }>>
    >("/appointments/slots", {
      params: { providerId, locationId, serviceId, date, timezone },
    });
    return response.data;
  },

  /**
   * Lookup appointments by booking reference
   * Works for both guest and logged-in customers
   */
  lookupAppointmentsByReference: async (bookingRef: string) => {
    const response = await api.get<ApiResponse<Appointment[]>>(
      "/appointments/lookup",
      { params: { bookingRef } }
    );
    return response.data;
  },

  /**
   * Check if a registered user exists with given phone/email
   * Used during guest booking to prompt users to log in for better experience
   * 
   * Returns:
   * - exists: true if a registered user has this phone/email
   * - hasExistingBookings: true if there are past guest bookings with this phone/email
   * - suggestion: 'login' | 'register' | 'continue'
   */
  checkExistingUser: async (phone: string, email?: string) => {
    const response = await api.get<ApiResponse<{
      exists: boolean;
      hasExistingBookings: boolean;
      existingBookingsCount: number;
      message: string;
      suggestion: 'login' | 'register' | 'continue';
    }>>(
      "/appointments/guest/check-existing-user",
      { params: { phone, email } }
    );
    return response.data;
  },
};

// Queue Services
export const queueService = {
  join: async (data: QueueJoinFormData) => {
    const response = await api.post<ApiResponse<QueueEntry>>(
      "/queue/join",
      data
    );
    return response.data;
  },

  getStatus: async (id: string) => {
    const response = await api.get<ApiResponse<QueueEntry>>(`/queue/${id}`);
    return response.data;
  },

  getMyQueue: async () => {
    const response = await api.get<PaginatedResponse<QueueEntry>>("/queue/my");
    return response.data;
  },

  getCurrentQueue: async (
    locationId: string,
    date: string,
    serviceId?: string
  ) => {
    const response = await api.get<PaginatedResponse<QueueEntry>>(
      "/queue/current",
      {
        params: { locationId, date, serviceId },
      }
    );
    return response.data;
  },

  cancel: async (id: string) => {
    const response = await api.patch<ApiResponse<QueueEntry>>(
      `/queue/${id}/cancel`
    );
    return response.data;
  },

  getEstimatedWaitTime: async (locationId: string, serviceId: string) => {
    const response = await api.get<ApiResponse<{ estimatedWaitTime: number }>>(
      "/queue/estimated-wait-time",
      {
        params: { locationId, serviceId },
      }
    );
    return response.data;
  },
};

// Combined Search Service
export const searchService = {
  searchAll: async (query: string, filters?: SearchFilters) => {
    const [services, providers, locations] = await Promise.all([
      serviceService.search(query, { category: filters?.category }),
      providerService.search(query, { category: filters?.category }),
      locationService.getAll({ city: query }),
    ]);

    return {
      services: services.data,
      providers: providers.data,
      locations: locations.data,
    };
  },
};
